import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, errMsg, setAuth, getProfile, getToken, API } from "./api";
import { Shell, Panel, BigBtn, GhostBtn, Field, Spinner, inputCls } from "./ui";

const TABS = ["Roster", "Trainers", "Glofox", "Messages", "Report", "Settings"];

function SALogin({ onDone }) {
  const [status, setStatus] = useState(null);
  const [mobile, setMobile] = useState("");
  const [pw, setPw] = useState("");
  const [mgrEmail, setMgrEmail] = useState("");
  const [mgrPw, setMgrPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/sched/auth/superadmin/status").then((r) => setStatus(r.data)); }, []);
  if (!status) return <Spinner />;
  const login = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const { data } = await api.post("/sched/auth/superadmin", { mobile, password: pw });
      setAuth(data.token, { name: data.name, role: "superadmin", kind: "superadmin" });
      onDone();
    } catch (ex) { setErr(errMsg(ex)); }
  };
  const bootstrap = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/sched/auth/superadmin/bootstrap", { manager_email: mgrEmail, manager_password: mgrPw, new_password: newPw });
      toast.success("Super-admin password set — now sign in");
      setStatus({ ...status, setup_required: false });
    } catch (ex) { setErr(errMsg(ex)); }
  };
  return (
    <Shell title="Super-admin" back="/staff/entry">
      {status.setup_required ? (
        <form onSubmit={bootstrap} className="space-y-4 max-w-sm mx-auto mt-6" data-testid="sa-bootstrap-form">
          <h2 className="text-2xl uppercase italic" style={{ fontFamily: "Anton, sans-serif" }}>Secure setup required</h2>
          <p className="text-white/55 text-sm">No super-admin password has been supplied. {status.name} must set one now — authorised by the venue manager account. Nothing is invented or emailed.</p>
          <input data-testid="sa-mgr-email" type="email" value={mgrEmail} onChange={(e) => setMgrEmail(e.target.value)} placeholder="Manager email" className={inputCls} />
          <input data-testid="sa-mgr-password" type="password" value={mgrPw} onChange={(e) => setMgrPw(e.target.value)} placeholder="Manager password" className={inputCls} />
          <input data-testid="sa-new-password" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New super-admin password (10+ chars)" className={inputCls} />
          {err && <p className="text-[#ff2e4d] text-sm" data-testid="sa-error">{err}</p>}
          <BigBtn data-testid="sa-bootstrap-button" type="submit">Set super-admin password</BigBtn>
        </form>
      ) : (
        <form onSubmit={login} className="space-y-4 max-w-sm mx-auto mt-6" data-testid="sa-login-form">
          <h2 className="text-2xl uppercase italic" style={{ fontFamily: "Anton, sans-serif" }}>Super-admin sign in</h2>
          <input data-testid="sa-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile (04xx xxx xxx)" inputMode="tel" className={inputCls} />
          <input data-testid="sa-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className={inputCls} />
          {err && <p className="text-[#ff2e4d] text-sm" data-testid="sa-error">{err}</p>}
          <BigBtn data-testid="sa-login-button" type="submit">Sign in</BigBtn>
        </form>
      )}
    </Shell>
  );
}

function Roster() {
  const [data, setData] = useState(null);
  const load = () => api.get("/sched/roster").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner />;
  const gaps = data.settings_gaps;
  const byDate = {};
  for (const o of data.occurrences) (byDate[o.date] = byDate[o.date] || []).push(o);
  const SC = { assigned: "text-white/60", covered: "text-emerald-400", uncovered: "text-[#ff2e4d]", cover_requested: "text-amber-400" };
  return (
    <div data-testid="sa-roster">
      {(!gaps.commencement_date || !gaps.duration) && (
        <Panel className="p-4 mb-4 border-amber-400/40" data-testid="roster-gaps">
          <p className="text-amber-400 text-sm font-semibold">Roster not activated</p>
          <p className="text-xs text-white/55">Set the commencement date and class duration in Settings, then tap Activate. The 17 weekly slots are seeded as drafts — nothing is guessed.</p>
        </Panel>
      )}
      {!gaps.sunday_rotation_configured && <p className="text-xs text-amber-400 mb-3">Sunday rotation not configured — Kate remains the provisional Sunday default.</p>}
      <GhostBtn data-testid="roster-activate-button" className="mb-4"
        onClick={async () => {
          try { const { data: d } = await api.post("/sched/roster/activate"); toast.success(`Roster activated — ${d.generated} classes published`); load(); }
          catch (e) { toast.error(errMsg(e)); }
        }}>Activate roster / regenerate horizon</GhostBtn>
      {Object.entries(byDate).map(([date, occs]) => (
        <div key={date} className="mb-3">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-1.5">{occs[0].day} {date}</p>
          <div className="space-y-1.5">
            {occs.map((o) => (
              <Panel key={o.occ_id} className="px-3 py-2 flex items-center justify-between gap-2" data-testid={`roster-occ-${o.occ_id}`}>
                <p className="text-sm">{o.start_12h} — <b>{o.assigned_trainer || "UNCOVERED"}</b>
                  {o.assigned_trainer !== o.original_trainer && o.original_trainer ? <span className="text-white/40"> (cover for {o.original_trainer})</span> : ""}
                  {o.provisional ? <span className="text-amber-400 text-xs"> · rotation not configured</span> : ""}
                </p>
                <span className={`text-xs ${SC[o.status] || "text-white/50"}`}>{o.status}{o.confirmation === "awaiting" ? " · awaiting conf." : ""}</span>
              </Panel>
            ))}
          </div>
        </div>
      ))}
      {data.occurrences.length === 0 && <p className="text-white/40 text-sm">No published classes in this window yet.</p>}
    </div>
  );
}

function Trainers() {
  const [trainers, setTrainers] = useState(null);
  const [edit, setEdit] = useState(null);
  const [mobile, setMobile] = useState("");
  const [pw, setPw] = useState("");
  const load = () => api.get("/sched/trainers").then((r) => setTrainers(r.data.trainers)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!trainers) return <Spinner />;
  const save = async (t) => {
    try {
      const body = {};
      if (mobile) body.mobile = mobile;
      if (pw) body.set_password = pw;
      await api.patch(`/sched/trainers/${t.trainer_id}`, body);
      toast.success(`${t.name} updated${pw ? " — temporary password set, deliver it privately" : ""}`);
      setEdit(null); setMobile(""); setPw(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid="sa-trainers">
      {trainers.map((t) => (
        <Panel key={t.trainer_id} className="p-3.5" data-testid={`trainer-row-${t.name}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold">{t.name.slice(0, 2).toUpperCase()}</span>
              <div>
                <p className="font-semibold">{t.name} {t.demo_portrait && <span className="text-[10px] text-white/30">(demo portrait placeholder)</span>}</p>
                <p className="text-xs text-white/45">
                  {t.phone ? t.phone : <span className="text-amber-400">Phone required</span>}
                  {" · "}{t.draft ? "Draft — login inactive" : "Login active"}
                  {" · SMS "}{t.sms_enabled ? "on" : "off"}
                </p>
              </div>
            </div>
            <div className="flex gap-1.5">
              <GhostBtn data-testid={`trainer-edit-${t.name}`} onClick={() => { setEdit(edit === t.trainer_id ? null : t.trainer_id); setMobile(""); setPw(""); }} className="text-xs">Edit</GhostBtn>
              <GhostBtn onClick={async () => { await api.patch(`/sched/trainers/${t.trainer_id}`, { sms_enabled: !t.sms_enabled }); load(); }} className="text-xs">SMS {t.sms_enabled ? "off" : "on"}</GhostBtn>
            </div>
          </div>
          {edit === t.trainer_id && (
            <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
              <input data-testid="trainer-mobile-edit" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Mobile (04xx xxx xxx)" className={inputCls} />
              <input data-testid="trainer-pw-edit" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Temporary password (8+ chars, delivered manually)" className={inputCls} />
              <GhostBtn data-testid="trainer-save-button" onClick={() => save(t)} className="w-full">Save</GhostBtn>
            </div>
          )}
        </Panel>
      ))}
    </div>
  );
}

function Glofox() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/sched/glofox").then((r) => setItems(r.data.items)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!items) return <Spinner />;
  return (
    <div data-testid="sa-glofox">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-white/55">Manual Glofox updates — the app never updates Glofox automatically.</p>
        <a href={`${API}/sched/glofox/export?token=${getToken()}`} className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-xl border border-white/20 text-xs"><Download size={13} />CSV</a>
      </div>
      {items.length === 0 && <p className="text-white/40 text-sm">No Glofox actions outstanding.</p>}
      <div className="space-y-2">
        {items.map((i) => (
          <Panel key={i.item_id} className="p-3.5 flex items-center justify-between gap-3" data-testid={`glofox-${i.item_id}`}>
            <p className="text-sm">{i.date} {i.start_local}: <b>{i.original} → {i.current}</b>
              <span className="block text-xs text-white/40">{i.reason} · {i.status}{i.acked_by ? ` by ${i.acked_by}` : ""}</span></p>
            {i.status === "open" && (
              <GhostBtn data-testid={`glofox-ack-${i.item_id}`} onClick={async () => { await api.post(`/sched/glofox/${i.item_id}/ack`); toast.success("Marked updated in Glofox"); load(); }} className="text-xs shrink-0">Updated in Glofox</GhostBtn>
            )}
          </Panel>
        ))}
      </div>
    </div>
  );
}

function Messages() {
  const [data, setData] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [simTid, setSimTid] = useState("");
  const [reply, setReply] = useState("");
  const load = () => api.get("/sched/outbox").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/sched/trainers").then((r) => setTrainers(r.data.trainers)); }, []);
  if (!data) return <Spinner />;
  return (
    <div data-testid="sa-messages">
      <Panel className="p-4 mb-4 space-y-2">
        <p className="text-xs uppercase tracking-widest text-white/40">SMS reply simulator (simulation only)</p>
        <div className="flex gap-2">
          <select data-testid="sim-trainer-select" value={simTid} onChange={(e) => setSimTid(e.target.value)} className={inputCls + " flex-1"}>
            <option value="">Sender trainer…</option>
            {trainers.map((t) => <option key={t.trainer_id} value={t.trainer_id}>{t.name}</option>)}
          </select>
          <input data-testid="sim-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Y / N / Y CODE" className={inputCls + " w-32"} />
          <GhostBtn data-testid="sim-send-button" onClick={async () => {
            try { const { data: d } = await api.post("/sched/simulator/reply", { trainer_id: simTid, reply }); toast.info(d.result); load(); }
            catch (e) { toast.error(errMsg(e)); }
          }}>Simulate</GhostBtn>
        </div>
      </Panel>
      <div className="space-y-1.5 max-h-[55vh] overflow-y-auto">
        {data.messages.map((m) => (
          <Panel key={m.msg_id} className="px-3 py-2">
            <p className="text-xs"><b className={m.status === "would_send" ? "text-sky-300" : "text-white/40"}>{m.status === "would_send" ? "WOULD SEND" : `SUPPRESSED (${m.suppress_reason})`}</b> → {m.to_name} · {m.type}</p>
            <p className="text-sm text-white/70">{m.body}</p>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function Report() {
  const [rep, setRep] = useState(null);
  useEffect(() => { api.get("/sched/report/preview").then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!rep) return <Spinner />;
  return (
    <div className="space-y-4" data-testid="sa-report">
      <p className="text-sm text-white/55">Weekly report to the configured report email · window {rep.window} · {rep.test_mode ? "TEST MODE — reports are held, not emailed" : "live sending enabled"}</p>
      <Panel className="p-4">
        <p className="text-sm font-semibold text-[#ff2e4d]">1. ACTION REQUIRED — uncovered next 7 days ({rep.action_required_uncovered.length})</p>
        {rep.action_required_uncovered.map((x) => <p key={x.occ_id} className="text-sm">• {x.day} {x.date} {x.start_12h} — original {x.original_trainer}</p>)}
        <p className="text-sm font-semibold text-amber-400 mt-3">Awaiting confirmation ({rep.awaiting_confirmation.length})</p>
        <p className="text-sm font-semibold text-sky-300 mt-3">2. UPDATE GLOFOX ({rep.update_glofox.length})</p>
        <p className="text-sm text-white/60 mt-3">3. 14-day roster: {rep.roster_14_days.length} classes · 4. Changes last week: {rep.changes_last_week.length} · 5. Sunday rotation configured: {String(rep.sunday_rotation.configured)}</p>
      </Panel>
      <BigBtn data-testid="report-send-button" onClick={async () => {
        try { const { data } = await api.post("/sched/report/send"); toast.success(`Report: ${data.status}`); }
        catch (e) { toast.error(errMsg(e)); }
      }}>Generate weekly report now</BigBtn>
    </div>
  );
}

function SchedSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/sched/settings").then((r) => setS(r.data.settings)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!s) return <Spinner />;
  const save = async () => {
    try {
      await api.patch("/sched/settings", {
        commencement_date: s.commencement_date, default_duration_min: s.default_duration_min ? Number(s.default_duration_min) : null,
        room: s.room, sim_clock_offset_min: Number(s.sim_clock_offset_min || 0), timing: s.timing,
      });
      toast.success("Scheduler settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="sa-settings">
      <p className="text-sm text-white/55">All timing defaults are visible and editable here. SMS mode: <b className="text-sky-300">SIMULATION — no texts sent</b>.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Commencement date"><input data-testid="set-commencement" type="date" value={s.commencement_date || ""} onChange={(e) => setS({ ...s, commencement_date: e.target.value })} className={inputCls} /></Field>
        <Field label="Class duration (min)"><input data-testid="set-duration" type="number" value={s.default_duration_min || ""} onChange={(e) => setS({ ...s, default_duration_min: e.target.value })} className={inputCls} /></Field>
        <Field label="Room (optional)"><input value={s.room || ""} onChange={(e) => setS({ ...s, room: e.target.value })} className={inputCls} /></Field>
        <Field label="Sim clock offset (minutes)"><input data-testid="set-sim-offset" type="number" value={s.sim_clock_offset_min} onChange={(e) => setS({ ...s, sim_clock_offset_min: e.target.value })} className={inputCls} /></Field>
        <Field label="Preferred window >48h (hrs)"><input type="number" value={s.timing.preferred_gt48h_hours} onChange={(e) => setS({ ...s, timing: { ...s.timing, preferred_gt48h_hours: Number(e.target.value) } })} className={inputCls} /></Field>
        <Field label="Preferred window 24–48h (hrs)"><input type="number" value={s.timing.preferred_24_48_hours} onChange={(e) => setS({ ...s, timing: { ...s.timing, preferred_24_48_hours: Number(e.target.value) } })} className={inputCls} /></Field>
        <Field label="Follow-up after broadcast (hrs)"><input type="number" value={s.timing.followup_hours} onChange={(e) => setS({ ...s, timing: { ...s.timing, followup_hours: Number(e.target.value) } })} className={inputCls} /></Field>
        <Field label="Repeat reminders every (hrs)"><input type="number" value={s.timing.repeat_hours} onChange={(e) => setS({ ...s, timing: { ...s.timing, repeat_hours: Number(e.target.value) } })} className={inputCls} /></Field>
        <Field label="Uncovered alert before class (hrs)"><input type="number" value={s.timing.alert_before_class_hours} onChange={(e) => setS({ ...s, timing: { ...s.timing, alert_before_class_hours: Number(e.target.value) } })} className={inputCls} /></Field>
      </div>
      <p className="text-xs text-white/40">Sunday rotation: not configured — Kate remains provisional. Supply the rotation order and anchor Sunday to enable it. Escalation contact: Dan 0415 853 322.</p>
      <BigBtn data-testid="set-save-button" onClick={save}>Save settings</BigBtn>
    </div>
  );
}

export default function SuperAdmin() {
  const [authed, setAuthed] = useState(getProfile()?.kind === "superadmin");
  const [tab, setTab] = useState("Roster");
  if (!authed) return <SALogin onDone={() => setAuthed(true)} />;
  const COMP = { Roster, Trainers, Glofox, Messages, Report, Settings: SchedSettings };
  const Active = COMP[tab];
  return (
    <Shell title="Scheduler admin" back="/staff/entry" wide>
      <div className="rounded-xl border border-sky-400/40 bg-sky-400/10 text-sky-300 text-xs px-3 py-2 mb-3" data-testid="simulation-banner">
        Simulation — no texts sent
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-3" data-testid="sa-tabs">
        {TABS.map((t) => (
          <button key={t} data-testid={`sa-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`min-h-[40px] px-4 rounded-full border text-sm whitespace-nowrap ${tab === t ? "border-[#ff2e4d] text-[#ff2e4d] bg-[#ff2e4d]/10" : "border-white/15 text-white/55"}`}>{t}</button>
        ))}
      </div>
      <Active />
    </Shell>
  );
}
